import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import diiLogo from '@/assets/dii-logo.png';
import { API_BASE, backendFetch } from "@/lib/backend";
import { useAuth } from "@/hooks/useAuth";

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signupSubmitted, setSignupSubmitted] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Only require terms acceptance during signup
    if (!isLogin && !agreedToTerms) {
      toast({
        title: "Agreement Required",
        description: "Please accept the User Agreement to continue.",
        variant: "destructive",
      });
      return;
    }

    if (!email || !password) {
      toast({
        title: "Missing Fields",
        description: "Please enter both email and password.",
        variant: "destructive",
      });
      return;
    }

    if (!isLogin) {
      // Validate signup fields
      if (!firstName || !lastName) {
        toast({
          title: "Missing Fields",
          description: "Please enter your first name and last name.",
          variant: "destructive",
        });
        return;
      }
      // Show confirmation message
      setSignupSubmitted(true);
      return;
    }

    setLoading(true);
    try {
      const res = await backendFetch("/api/auth/password/login", {
        method: "POST",
        retryOn401: false,
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (res.status === 204) {
        await refreshUser();
        navigate("/", { replace: true });
        return;
      }

      if (res.status === 401) {
        toast({
          title: "Invalid credentials",
          description: "Check your email/password and try again.",
          variant: "destructive",
        });
        return;
      }

      const text = await res.text().catch(() => "");
      toast({
        title: "Login failed",
        description: text ? `Server responded ${res.status}: ${text}` : `Server responded ${res.status}.`,
        variant: "destructive",
      });
    } catch (err) {
      toast({
        title: "Login failed",
        description: err instanceof Error ? err.message : "Unexpected error.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    // Backend will redirect to Google and then back to our frontend with auth cookies set.
    window.location.assign(`${API_BASE}/api/auth/google/login`);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden" style={{ background: 'linear-gradient(145deg, #1a2332 0%, #0f1922 40%, #162230 100%)' }}>
        {/* Subtle grain texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'1\'/%3E%3C/svg%3E")', backgroundSize: '128px 128px' }} />
        {/* Accent glow */}
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full opacity-[0.06]" style={{ background: 'radial-gradient(circle, #4a90d9 0%, transparent 70%)' }} />
        <div className="relative z-10 flex flex-col justify-between px-16 py-14 w-full">
          <div />
          <div>
            <h1 className="text-[68px] font-medium tracking-[-0.02em] leading-[1.08] text-white" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              AI&#8209;powered Deal<br />Analysis
            </h1>
            <p className="mt-8 text-[15px] leading-relaxed text-white/45 max-w-md">
              We automate the full Deals lifecycle, from the initial RFI to the final report.
            </p>
          </div>
          <div className="space-y-5">
            <div className="flex gap-6 text-[11px] tracking-wide text-white/25">
              <span>RFI</span>
              <span>Q&A</span>
              <span>Databook</span>
              <span>Report</span>
            </div>
            <div className="text-white/20 text-[11px]">
              © 2026 Diligence Technologies Limited
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          {signupSubmitted ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold tracking-tight">Application Received</h2>
              <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                Thank you for your interest! We've received your application and will review it shortly. 
                You'll receive an email with the next steps within 24 hours.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSignupSubmitted(false);
                  setIsLogin(true);
                  setFirstName("");
                  setLastName("");
                  setEmail("");
                  setPassword("");
                  setAgreedToTerms(false);
                }}
                className="mt-4"
              >
                Back to Sign In
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-2 text-center">
                <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 28, fontWeight: 600 }}>
                  {isLogin ? "Welcome back" : "Request Access"}
                </h2>
                {!isLogin && (
                  <p className="text-muted-foreground text-sm">
                    Fill in your details to request platform access
                  </p>
                )}
              </div>

              <form onSubmit={handleAuth} className="space-y-5">
                {!isLogin && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-sm font-medium">
                        First Name
                      </Label>
                      <Input
                        id="firstName"
                        type="text"
                        placeholder="John"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="h-11"
                        autoComplete="given-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-sm font-medium">
                        Last Name
                      </Label>
                      <Input
                        id="lastName"
                        type="text"
                        placeholder="Smith"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="h-11"
                        autoComplete="family-name"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 pr-10"
                      autoComplete={isLogin ? "current-password" : "new-password"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {isLogin && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-xs text-muted-foreground hover:text-accent transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}
                </div>

                {/* Only show terms checkbox during signup */}
                {!isLogin && (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="terms"
                        checked={agreedToTerms}
                        onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                        className="mt-0.5"
                      />
                      <label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed">
                        I agree to the{" "}
                        <button
                          type="button"
                          onClick={() => setShowAgreement(true)}
                          className="text-accent hover:underline font-medium"
                        >
                          Platform Terms of Use
                        </button>{" "}
                        and{" "}
                        <button
                          type="button"
                          onClick={() => setShowPrivacy(true)}
                          className="text-accent hover:underline font-medium"
                        >
                          Privacy Policy
                        </button>
                      </label>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="ghost"
                  className="w-full h-11 text-white hover:text-white hover:opacity-90 border-0 shadow-sm"
                  style={{ background: 'linear-gradient(145deg, #1a2332 0%, #0f1922 40%, #162230 100%)' }}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      {isLogin ? "Signing in..." : "Submitting..."}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {isLogin ? "Sign in" : "Submit Application"}
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11"
                  onClick={() => {
                    setLoading(true);
                    window.location.assign(`${API_BASE}/api/auth/microsoft/login`);
                  }}
                  disabled={loading}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23">
                    <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                    <path fill="#f35325" d="M1 1h10v10H1z" />
                    <path fill="#81bc06" d="M12 1h10v10H12z" />
                    <path fill="#05a6f0" d="M1 12h10v10H1z" />
                    <path fill="#ffba08" d="M12 12h10v10H12z" />
                  </svg>
                  Continue with Microsoft
                </Button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setAgreedToTerms(false);
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {isLogin ? (
                    <>Don't have an account? <span className="text-accent font-medium">Sign up</span></>
                  ) : (
                    <>Already have an account? <span className="text-accent font-medium">Sign in</span></>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Platform Terms of Use Modal */}
      {showAgreement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/50 backdrop-blur-sm">
          <div className="bg-card rounded-lg shadow-premium-lg max-w-2xl w-full max-h-[80vh] overflow-hidden animate-scale-in">
            <div className="p-6 border-b border-border">
              <h3 className="text-xl font-semibold">Platform Terms of Use</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Please read and accept the following terms
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[50vh] space-y-4 text-sm text-muted-foreground leading-relaxed">
              <section>
                <h4 className="font-semibold text-foreground mb-2">1. Application of Terms</h4>
                <p className="mb-2">
                  <strong className="text-foreground">1.1</strong> These Terms apply to your use of the Platform. By accessing and using the Platform:
                </p>
                <ul className="list-disc pl-6 mb-2 space-y-1">
                  <li>you agree to these Terms; and</li>
                  <li>where your access and use is on behalf of another person (e.g. a company), you confirm that you are authorised to, and do in fact, agree to these Terms on that person's behalf and that, by agreeing to these Terms on that person's behalf, that person is bound by these Terms.</li>
                </ul>
                <p>
                  <strong className="text-foreground">1.2</strong> If you do not agree to these Terms, you are not authorised to access and use the Platform, and you must immediately stop doing so.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">2. Changes</h4>
                <p className="mb-2">
                  <strong className="text-foreground">2.1</strong> We may change these Terms at any time by updating them on the Platform. Unless stated otherwise, any change takes effect immediately. You are responsible for ensuring you are familiar with the latest Terms. By continuing to access and use the Platform, you agree to be bound by the changed Terms.
                </p>
                <p className="mb-2">
                  <strong className="text-foreground">2.2</strong> We may change, suspend, discontinue, or restrict access to, the Platform without notice or liability.
                </p>
                <p>
                  <strong className="text-foreground">2.3</strong> These Terms were last updated on 04 January 2026.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">3. Definitions</h4>
                <p className="mb-2">In these Terms:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong className="text-foreground">Including</strong> and similar words do not imply any limit</li>
                  <li><strong className="text-foreground">Loss</strong> includes loss of profits, savings, revenue or data, and any other claim, damage, loss, liability and cost, including legal costs on a solicitor and own client basis</li>
                  <li><strong className="text-foreground">Personal information</strong> means information about an identifiable, living person</li>
                  <li><strong className="text-foreground">Terms</strong> means these terms and conditions titled Platform Terms of Use</li>
                  <li><strong className="text-foreground">Underlying System</strong> means any network, system, software, data or material that underlies or is connected to the Platform</li>
                  <li><strong className="text-foreground">User ID</strong> means a unique name and/or password allocated to you to allow you to access certain parts of the Platform</li>
                  <li><strong className="text-foreground">We, us or our</strong> means Diligence Technologies Limited</li>
                  <li><strong className="text-foreground">Platform</strong> means https://app.dill-y.com/</li>
                  <li><strong className="text-foreground">You</strong> means you or, if clause 1.1b applies, both you and the other person on whose behalf you are acting.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">4. Your Obligations</h4>
                <p className="mb-2">
                  <strong className="text-foreground">4.1</strong> You must provide true, current and complete information in your dealings with us (including when setting up an account), and must promptly update that information as required so that the information remains true, current and complete.
                </p>
                <p className="mb-2">
                  <strong className="text-foreground">4.2</strong> If you are given a User ID, you must keep your User ID secure and:
                </p>
                <ul className="list-disc pl-6 mb-2 space-y-1">
                  <li>not permit any other person to use your User ID, including not disclosing or providing it to any other person; and</li>
                  <li>immediately notify us if you become aware of any disclosure or unauthorised use of your User ID, by sending an email to service@dill-y.com.</li>
                </ul>
                <p className="mb-2">
                  <strong className="text-foreground">4.3</strong> You must:
                </p>
                <ul className="list-disc pl-6 mb-2 space-y-1">
                  <li>not act in a way, or use or introduce anything (including any virus, worm, Trojan horse, timebomb, keystroke logger, spyware or other similar feature) that in any way compromises, or may compromise, the Platform or any Underlying System, or otherwise attempt to damage or interfere with the Platform or any Underlying System; and</li>
                  <li>unless with our agreement, access the Platform via standard web browsers only and not by any other method.</li>
                </ul>
                <p className="mb-2">
                  <strong className="text-foreground">4.4</strong> You must obtain our written permission to establish a link to our Platform. If you wish to do so, email your request to service@dill-y.com.
                </p>
                <p>
                  <strong className="text-foreground">4.5</strong> You indemnify us against all Loss we suffer or incur as a direct or indirect result of your failure to comply with these Terms.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">5. Intellectual Property</h4>
                <p>
                  We (and our licensors) own all proprietary and intellectual property rights in the Platform (including all information, data, text, graphics, artwork, photographs, logos, icons, sound recordings, videos and look and feel), and the Underlying Systems.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">6. Disclaimers</h4>
                <p className="mb-2">
                  <strong className="text-foreground">6.1</strong> To the extent permitted by law, we and our licensors have no liability or responsibility to you or any other person for any Loss in connection with:
                </p>
                <ul className="list-disc pl-6 mb-2 space-y-1">
                  <li>the Platform being unavailable (in whole or in part) or performing slowly;</li>
                  <li>any error in, or omission from, any information made available through the Platform;</li>
                  <li>any exposure to viruses or other forms of interference which may damage your computer system or expose you to fraud when you access or use the Platform; and</li>
                  <li>any site linked from the Platform.</li>
                </ul>
                <p>
                  <strong className="text-foreground">6.2</strong> We make no representation or warranty that the Platform is appropriate or available for use in all countries or that the content satisfies the laws of all countries.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">7. Liability</h4>
                <p className="mb-2">
                  <strong className="text-foreground">7.1</strong> To the maximum extent permitted by law:
                </p>
                <ul className="list-disc pl-6 mb-2 space-y-1">
                  <li>you access and use the Platform at your own risk; and</li>
                  <li>we are not liable or responsible to you or any other person for any Loss under or in connection with these Terms, the Platform, or your access and use of (or inability to access or use) the Platform.</li>
                </ul>
                <p className="mb-2">
                  <strong className="text-foreground">7.2</strong> Except to the extent permitted by law, nothing in these Terms has the effect of contracting out of the New Zealand Consumer Guarantees Act 1993 or any other consumer protection law that cannot be excluded.
                </p>
                <p>
                  <strong className="text-foreground">7.3</strong> To the maximum extent permitted by law, our total liability to you under or in connection with these Terms is limited to the total fees paid or payable by the Customer to us in the 12 months preceding the event giving rise to the claim.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">8. Suspension and Termination</h4>
                <p className="mb-2">
                  <strong className="text-foreground">8.1</strong> Without prejudice to any other right or remedy available to us, if we consider that you have breached these Terms or we otherwise consider it appropriate, we may immediately, and without notice, suspend or terminate your access to the Platform (or any part of it).
                </p>
                <p>
                  <strong className="text-foreground">8.2</strong> On suspension or termination, you must immediately cease using the Platform and must not attempt to gain further access.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">9. General</h4>
                <p className="mb-2">
                  <strong className="text-foreground">9.1</strong> If we need to contact you, we may do so by email or by posting a notice on the Platform. You agree that this satisfies all legal requirements in relation to written communications.
                </p>
                <p className="mb-2">
                  <strong className="text-foreground">9.2</strong> These Terms, and any dispute relating to these Terms or the Platform, are governed by and must be interpreted in accordance with the laws of New Zealand. Each party submits to the non-exclusive jurisdiction of the Courts of New Zealand in relation to any dispute connected with these Terms or the Platform.
                </p>
                <p className="mb-2">
                  <strong className="text-foreground">9.3</strong> For us to waive a right under these Terms, the waiver must be in writing.
                </p>
                <p>
                  <strong className="text-foreground">9.4</strong> Clauses which, by their nature, are intended to survive termination of these Terms, including clauses 4.5, 5, 6, 7, 9.1, continue in force.
                </p>
              </section>
            </div>

            <div className="p-6 border-t border-border flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowAgreement(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setAgreedToTerms(true);
                  setShowAgreement(false);
                }}
                className="btn-premium"
              >
                Accept Terms
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/50 backdrop-blur-sm">
          <div className="bg-card rounded-lg shadow-premium-lg max-w-2xl w-full max-h-[80vh] overflow-hidden animate-scale-in">
            <div className="p-6 border-b border-border">
              <h3 className="text-xl font-semibold">Privacy Policy</h3>
              <p className="text-sm text-muted-foreground mt-1">
                How we collect, use, and protect your information
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[50vh] space-y-4 text-sm text-muted-foreground leading-relaxed">
              <section>
                <h4 className="font-semibold text-foreground mb-2">Introduction</h4>
                <p className="mb-2">
                  Diligence Technologies Limited (we, us, our) complies with the New Zealand Privacy Act 2020 (the Act) when dealing with personal information. Personal information is information about an identifiable individual (a natural person). This policy sets out how we will collect, use, disclose and protect your personal information.
                </p>
                <p>
                  This policy does not limit or exclude any of your rights under the Act. If you wish to seek further information on the Act, see{" "}
                  <a href="https://www.privacy.org.nz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    www.privacy.org.nz
                  </a>.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">Changes to This Policy</h4>
                <p className="mb-2">
                  We may change this policy by uploading a revised policy onto the website. The change will apply from the date that we upload the revised policy.
                </p>
                <p>
                  This policy was last updated on 04 January 2026.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">Who Do We Collect Your Personal Information From</h4>
                <p className="mb-2">We collect personal information about you from:</p>
                <ul className="list-disc pl-6 mb-2 space-y-1">
                  <li>you, when you provide that personal information to us, including via the website and any related service, through any registration or subscription process, through any contact with us (e.g. telephone call or email), or when you buy or use our services and products</li>
                  <li>third parties where you have authorised this or the information is publicly available.</li>
                </ul>
                <p>
                  If possible, we will collect personal information from you directly.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">How We Use Your Personal Information</h4>
                <p className="mb-2">We may use your personal information:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>to verify your identity</li>
                  <li>to provide services and products to you</li>
                  <li>to market our services and products to you, including contacting you electronically (e.g. by text or email for this purpose)</li>
                  <li>to improve the services and products that we provide to you</li>
                  <li>to bill you and to collect money that you owe us, including authorising and processing credit card transactions</li>
                  <li>to respond to communications from you, including a complaint</li>
                  <li>to conduct research and statistical analysis (on an anonymised basis)</li>
                  <li>to protect and/or enforce our legal rights and interests, including defending any claim</li>
                  <li>for any other purpose authorised by you or the Act.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">Disclosing Your Personal Information</h4>
                <p className="mb-2">We may disclose your personal information to:</p>
                <ul className="list-disc pl-6 mb-2 space-y-1">
                  <li>any business that supports our services and products, including any person that hosts or maintains any underlying IT system or data centre that we use to provide the website or other services and products</li>
                  <li>a credit reference agency for the purpose of credit checking you</li>
                  <li>other third parties (for anonymised statistical information)</li>
                  <li>a person who can require us to supply your personal information (e.g. a regulatory authority)</li>
                  <li>any other person authorised by the Act or another law (e.g. a law enforcement agency)</li>
                  <li>any other person authorised by you.</li>
                </ul>
                <p>
                  We may transfer your information in the case of a sale, merger, consolidation, liquidation, reorganisation or acquisition.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">Protecting Your Personal Information</h4>
                <p>
                  We will take reasonable steps to keep your personal information safe from loss, unauthorised activity, or other misuse.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">Accessing and Correcting Your Personal Information</h4>
                <p className="mb-2">
                  Subject to certain grounds for refusal set out in the Act, you have the right to access your readily retrievable personal information that we hold and to request a correction to your personal information. Before you exercise this right, we will need evidence to confirm that you are the individual to whom the personal information relates.
                </p>
                <p className="mb-2">
                  In respect of a request for correction, if we think the correction is reasonable and we are reasonably able to change the personal information, we will make the correction. If we do not make the correction, we will take reasonable steps to note on the personal information that you requested the correction.
                </p>
                <p className="mb-2">
                  If you want to exercise either of the above rights, email us at{" "}
                  <a href="mailto:service@dill-y.com" className="text-primary hover:underline">
                    service@dill-y.com
                  </a>. Your email should provide evidence of who you are and set out the details of your request (e.g. the personal information, or the correction, that you are requesting).
                </p>
                <p>
                  We may charge you our reasonable costs of providing to you copies of your personal information or correcting that information.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">Internet Use</h4>
                <p className="mb-2">
                  While we take reasonable steps to maintain secure internet connections, if you provide us with personal information over the internet, the provision of that information is at your own risk.
                </p>
                <p>
                  If you follow a link on our website to another site, the owner of that site will have its own privacy policy relating to your personal information. We suggest you review that site's privacy policy before you provide personal information.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">Cookies</h4>
                <p>
                  We use cookies (an alphanumeric identifier that we transfer to your computer's hard drive so that we can recognise your browser) to monitor your use of the website. You may disable cookies by changing the settings on your browser, although this may mean that you cannot use all of the features of the website.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-foreground mb-2">Contacting Us</h4>
                <p>
                  If you have any questions about this privacy policy, our privacy practices, or if you would like to request access to, or correction of, your personal information, you can contact us at{" "}
                  <a href="mailto:service@dill-y.com" className="text-primary hover:underline">
                    service@dill-y.com
                  </a>.
                </p>
              </section>
            </div>

            <div className="p-6 border-t border-border flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowPrivacy(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setAgreedToTerms(true);
                  setShowPrivacy(false);
                }}
                className="btn-premium"
              >
                Accept Policy
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/50 backdrop-blur-sm">
          <div className="bg-card rounded-lg shadow-premium-lg max-w-md w-full overflow-hidden animate-scale-in">
            <div className="p-6 border-b border-border">
              <h3 className="text-xl font-semibold">Reset Password</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Enter your email address and we'll send you instructions to reset your password.
              </p>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!forgotEmail) return;
                setForgotLoading(true);
                try {
                  const res = await backendFetch("/api/auth/password/reset", {
                    method: "POST",
                    retryOn401: false,
                    headers: { "content-type": "application/json" },
                    body: JSON.stringify({ email: forgotEmail }),
                  });
                  toast({
                    title: "Check your email",
                    description: "If an account exists with that email, you'll receive reset instructions shortly.",
                  });
                  setShowForgotPassword(false);
                  setForgotEmail("");
                } catch {
                  toast({
                    title: "Something went wrong",
                    description: "Please try again later.",
                    variant: "destructive",
                  });
                } finally {
                  setForgotLoading(false);
                }
              }}
              className="p-6 space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-sm font-medium">
                  Email address
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="name@company.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="h-11"
                  autoComplete="email"
                  autoFocus
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotEmail("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn-premium"
                  disabled={forgotLoading || !forgotEmail}
                >
                  {forgotLoading ? "Sending..." : "Send Reset Link"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}